import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { colorsPalettes, setFont } from "@similarweb/styles";

import {
    TOGGLE_ON_PERSISTENT_QUERY_BAR,
    TOGGLE_OFF_COMPARE_DROPDOWN,
} from "app/action_types/website_analysis_action-types";
import { i18nFilter } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import I18n from "pro-features/components/WithTranslation/src/I18n";

const i18n = i18nFilter();

type QueryBarItemType = "website" | "keyword" | "advertiser" | "publisher";

export const queryBarItemTypes: Record<QueryBarItemType, string> = {
    website: "querybar.missing.param.state.query.item.website",
    keyword: "querybar.missing.param.state.query.item.keyword",
    advertiser: "querybar.missing.param.state.query.item.advertiser",
    publisher: "querybar.missing.param.state.query.item.publisher",
};

interface IDefaultLocale {
    title: string;
    subtitle: string;
}

const defaultLocale = {
    title: "querybar.missing.param.state.title",
    subtitle: "querybar.missing.param.state.subtitle",
};

const image = AssetsService.assetUrl("/images/homepage/empty-query-bar.svg");

interface IMandatoryParamsEmptyStateProps {
    missingItemType?: QueryBarItemType;
    locale?: IDefaultLocale;
}

export const MandatoryParamsEmptyState = ({
    missingItemType = "website",
    locale = defaultLocale,
}: IMandatoryParamsEmptyStateProps) => (
    <EmptyPageContainer>
        <img
            src={image}
            alt=""
        />
        <Title>{locale.title}</Title>
        <Subtitle dataObj={{ itemType: i18n(queryBarItemTypes[missingItemType]) }}>{locale.subtitle}</Subtitle>
    </EmptyPageContainer>
);

const EmptyPageContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    img {
        margin-bottom: 20px;
        width: 128px;
    }
`;

const Title = styled(I18n)`
    ${setFont({ $size: 16, $color: colorsPalettes.carbon[500] })}
`;
const Subtitle = styled(I18n)`
    ${setFont({ $size: 12, $color: colorsPalettes.carbon[300] })}
`;

export const AccessWithoutMandatoryParamsHOC = <P extends object>(
    Component: FunctionComponent<P>,
    mandatoryParams: string | string[],
    missingItemType: QueryBarItemType = "website",
    locale: IDefaultLocale = defaultLocale,
    // The page is also accessible when ANY of these params is present, regardless
    // of the mandatory ones (e.g. an industry-mode entity carried in its own param).
    accessibleWhenPresent: string[] = [],
) => {
    const AccessWithoutMandatoryParams = (props: P) => {
        const { params } = useCurrentStateAndParams();
        const dispatch = useDispatch();

        const lacksSomeParams = useMemo(() => {
            if (accessibleWhenPresent.some((param) => params[param] != null)) {
                return false;
            }
            const mandatoryParamsArray = typeof mandatoryParams === "string" ? [mandatoryParams] : mandatoryParams;
            return mandatoryParamsArray.some((param) => params[param] == null);
        }, [params]);

        useEffect(() => {
            if (lacksSomeParams) {
                dispatch({ type: TOGGLE_ON_PERSISTENT_QUERY_BAR });
            } else {
                dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
            }
        }, [lacksSomeParams]);

        return !lacksSomeParams ? (
            <Component {...props} />
        ) : (
            <MandatoryParamsEmptyState
                missingItemType={missingItemType}
                locale={locale}
            />
        );
    };

    return AccessWithoutMandatoryParams;
};
